// The Resourcing page's API client (apps/server/src/resourcing.mjs): the
// planner's payload for a range of months, New assignment, and reassigning
// one month (drag to reassign, and its Undo). Undoing New assignment deletes
// the rows it made through the object API.
import { ObjectsApiError } from '../objects/objectsApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export type AssignmentStatus = 'forecast' | 'assigned' | 'confirmed';

export interface ResourceRole {
  id: string;
  name: string;
  sort_order: number;
  active: boolean;
}

export interface ResourcePerson {
  id: string;
  name: string;
  /** Usual monthly hours; null: not in delivery. */
  monthly_capacity_hours: number | null;
  primary_role_id: string | null;
  /** Every role they fill, primary first. */
  role_ids: string[];
}

/** resource_period: a person's month. */
export interface PersonPeriod {
  app_user_id: string;
  /** YYYY-MM. */
  month: string;
  capacity_hours: number;
  confirmed_hours: number;
  assigned_hours: number;
  booked_hours: number;
  available_hours: number;
  over_hours: number;
  utilization_pct: number | null;
  logged_hours: number;
  billed_hours: number;
  capacity_note: string | null;
}

/** resource_role_period: a role's month, by its people's primary role. */
export interface RolePeriod {
  resource_role_id: string;
  month: string;
  capacity_hours: number;
  booked_hours: number;
  available_hours: number;
  unstaffed_hours: number;
  net_hours: number;
}

/** resource_project_period: a person (or nobody yet) on a project or deal in a month. */
export interface ProjectPeriod {
  app_user_id: string | null;
  resource_role_id: string | null;
  opportunity_id: string | null;
  project_id: string | null;
  month: string;
  confirmed_hours: number;
  assigned_hours: number;
  forecast_hours: number;
  probability: number;
  weighted_hours: number;
  logged_hours: number;
  billed_hours: number;
}

export interface ResourceAssignment {
  id: string;
  opportunity_id: string | null;
  project_id: string | null;
  account_id: string;
  resource_role_id: string;
  app_user_id: string | null;
  month: string;
  hours: number;
  status: AssignmentStatus;
  source: string;
  edited_at: string | null;
}

/** A project (won work) or an opportunity (an open deal, or won with no project yet). */
export interface ResourceWork {
  kind: 'project' | 'opportunity';
  id: string;
  /** The deal the work came from (a deal's own id); its role split is set there. */
  opportunity_id: string | null;
  name: string;
  account_id: string;
  account_name: string;
  /** A project's status, or a deal's stage. */
  stage: string;
  won: boolean;
  probability: number;
  /** The deal's largest revenue stream, when it has a revenue schedule. */
  stream: string | null;
  first_month: string | null;
  last_month: string | null;
}

/** What a piece of work needs of a role in a month, assigned or not: its sold hours, split by role. */
export interface ResourceDemand {
  opportunity_id: string;
  /** The project a won deal became, which holds its hours. */
  project_id: string | null;
  resource_role_id: string;
  month: string;
  hours: number;
}

export interface ResourcingData {
  from: string;
  to: string;
  months: string[];
  current_month: string;
  roles: ResourceRole[];
  people: ResourcePerson[];
  periods: PersonPeriod[];
  role_periods: RolePeriod[];
  project_periods: ProjectPeriod[];
  assignments: ResourceAssignment[];
  work: ResourceWork[];
  demand: ResourceDemand[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const list = (value: unknown): Record<string, unknown>[] => (Array.isArray(value) ? value.filter(isRecord) : []);
const str = (value: unknown): string => (typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value));
const optStr = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);
/** A figure: null and anything not a number read as 0. */
const num = (value: unknown): number => {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : 0;
};
const optNum = (value: unknown): number | null => (value === null || value === undefined || value === '' ? null : num(value));
const STATUSES = new Set(['forecast', 'assigned', 'confirmed']);

/** The payload as the page uses it: every list present, every figure a number (null hours are 0). */
export function parseResourcing(payload: unknown): ResourcingData {
  const p = isRecord(payload) ? payload : {};
  return {
    from: str(p.from),
    to: str(p.to),
    months: Array.isArray(p.months) ? p.months.filter((m): m is string => typeof m === 'string') : [],
    current_month: str(p.current_month),
    roles: list(p.roles).map((r) => ({ id: str(r.id), name: str(r.name), sort_order: num(r.sort_order), active: r.active !== false })),
    people: list(p.people).map((u) => ({
      id: str(u.id),
      name: str(u.name) || 'Unnamed person',
      monthly_capacity_hours: optNum(u.monthly_capacity_hours),
      primary_role_id: optStr(u.primary_role_id),
      role_ids: Array.isArray(u.role_ids) ? u.role_ids.filter((r): r is string => typeof r === 'string') : [],
    })),
    periods: list(p.periods).map((r) => ({
      app_user_id: str(r.app_user_id),
      month: str(r.month),
      capacity_hours: num(r.capacity_hours),
      confirmed_hours: num(r.confirmed_hours),
      assigned_hours: num(r.assigned_hours),
      booked_hours: num(r.booked_hours),
      available_hours: num(r.available_hours),
      over_hours: num(r.over_hours),
      utilization_pct: optNum(r.utilization_pct),
      logged_hours: num(r.logged_hours),
      billed_hours: num(r.billed_hours),
      capacity_note: optStr(r.capacity_note),
    })),
    role_periods: list(p.role_periods).map((r) => ({
      resource_role_id: str(r.resource_role_id),
      month: str(r.month),
      capacity_hours: num(r.capacity_hours),
      booked_hours: num(r.booked_hours),
      available_hours: num(r.available_hours),
      unstaffed_hours: num(r.unstaffed_hours),
      net_hours: num(r.net_hours),
    })),
    project_periods: list(p.project_periods).map((r) => ({
      app_user_id: optStr(r.app_user_id),
      resource_role_id: optStr(r.resource_role_id),
      opportunity_id: optStr(r.opportunity_id),
      project_id: optStr(r.project_id),
      month: str(r.month),
      confirmed_hours: num(r.confirmed_hours),
      assigned_hours: num(r.assigned_hours),
      forecast_hours: num(r.forecast_hours),
      probability: r.probability === null || r.probability === undefined ? 100 : num(r.probability),
      weighted_hours: num(r.weighted_hours),
      logged_hours: num(r.logged_hours),
      billed_hours: num(r.billed_hours),
    })),
    assignments: list(p.assignments)
      .filter((a) => STATUSES.has(str(a.status)))
      .map((a) => ({
        id: str(a.id),
        opportunity_id: optStr(a.opportunity_id),
        project_id: optStr(a.project_id),
        account_id: str(a.account_id),
        resource_role_id: str(a.resource_role_id),
        app_user_id: optStr(a.app_user_id),
        month: str(a.month),
        hours: num(a.hours),
        status: str(a.status) as AssignmentStatus,
        source: str(a.source),
        edited_at: optStr(a.edited_at),
      })),
    work: list(p.work)
      .filter((w) => w.kind === 'project' || w.kind === 'opportunity')
      .map((w) => ({
        kind: w.kind as 'project' | 'opportunity',
        id: str(w.id),
        opportunity_id: optStr(w.opportunity_id),
        name: str(w.name),
        account_id: str(w.account_id),
        account_name: str(w.account_name) || str(w.name),
        stage: str(w.stage),
        won: w.won === true,
        probability: num(w.probability),
        stream: optStr(w.stream),
        first_month: optStr(w.first_month),
        last_month: optStr(w.last_month),
      })),
    demand: list(p.demand)
      .map((d) => ({
        opportunity_id: str(d.opportunity_id),
        project_id: optStr(d.project_id),
        resource_role_id: str(d.resource_role_id),
        month: str(d.month),
        hours: num(d.hours),
      }))
      .filter((d) => d.hours > 0),
  };
}

async function errorOf(response: Response, fallback: string): Promise<ObjectsApiError> {
  let message = fallback;
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string' && body.error !== '') message = body.error;
  } catch {
    // Not JSON: the fallback says enough.
  }
  return new ObjectsApiError(response.status, message);
}

export async function fetchResourcing(fetcher: Fetch, from: string, to: string): Promise<ResourcingData> {
  const response = await fetcher(`/api/resourcing?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  if (!response.ok) throw await errorOf(response, 'Could not load the resource plan.');
  return parseResourcing(await response.json());
}

export interface NewAssignment {
  project_id?: string;
  opportunity_id?: string;
  resource_role_id: string;
  app_user_id: string | null;
  from: string;
  to: string;
  hours: number;
}

export async function createAssignments(
  fetcher: Fetch,
  body: NewAssignment,
): Promise<{ status: AssignmentStatus; ids: string[] }> {
  const response = await fetcher('/api/resource-assignments', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await errorOf(response, 'Could not add the assignment.');
  const payload = (await response.json()) as { status?: AssignmentStatus; assignments?: { id?: unknown }[] };
  return { status: payload.status ?? 'forecast', ids: (payload.assignments ?? []).map((a) => str(a.id)).filter(Boolean) };
}

/** Names who fills one month (null: nobody yet), and answers who did before, for Undo. */
export async function reassignAssignment(
  fetcher: Fetch,
  id: string,
  appUserId: string | null,
): Promise<{ previous: { app_user_id: string | null; status: AssignmentStatus } }> {
  const response = await fetcher(`/api/resource-assignments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ app_user_id: appUserId }),
  });
  if (!response.ok) throw await errorOf(response, 'Could not move the assignment.');
  const payload = (await response.json()) as { previous?: { app_user_id?: unknown; status?: unknown } };
  return {
    previous: {
      app_user_id: optStr(payload.previous?.app_user_id),
      status: (STATUSES.has(str(payload.previous?.status)) ? str(payload.previous?.status) : 'assigned') as AssignmentStatus,
    },
  };
}

/** Changes one month's hours, marking it edited; answers the hours before, for Undo. */
export async function updateAssignmentHours(fetcher: Fetch, id: string, hours: number): Promise<{ previous: number }> {
  const response = await fetcher(`/api/resource-assignments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ hours }),
  });
  if (!response.ok) throw await errorOf(response, 'Could not change the hours.');
  const payload = (await response.json()) as { previous?: { hours?: unknown } };
  return { previous: num(payload.previous?.hours) };
}

export async function deleteAssignment(fetcher: Fetch, id: string): Promise<void> {
  const response = await fetcher(`/api/objects/resource-assignments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!response.ok && response.status !== 404) throw await errorOf(response, 'Could not undo the assignment.');
}

// ── Sold vs actual role split (GET/PUT/DELETE /api/resource-splits/:opportunityId) ──

export interface RoleShare {
  resource_role_id: string;
  share_pct: number;
}
export interface ResourceSplit {
  opportunity_id: string;
  /** The products' split, weighted by the quote's hours. Empty when the quote has no hours. */
  sold: (RoleShare & { hours: number })[];
  /** The deal's own split, or null when the sold split applies. */
  actual: RoleShare[] | null;
  actual_updated_at: string | null;
  /** Hours on the deal and its projects now, by role. */
  held: { resource_role_id: string; hours: number }[];
}

export function parseSplit(payload: unknown): ResourceSplit {
  const p = isRecord(payload) ? payload : {};
  const share = (r: Record<string, unknown>): RoleShare => ({ resource_role_id: str(r.resource_role_id), share_pct: num(r.share_pct) });
  return {
    opportunity_id: str(p.opportunity_id),
    sold: list(p.sold).map((r) => ({ ...share(r), hours: num(r.hours) })),
    actual: Array.isArray(p.actual) ? list(p.actual).map(share) : null,
    actual_updated_at: optStr(p.actual_updated_at),
    held: list(p.held).map((r) => ({ resource_role_id: str(r.resource_role_id), hours: num(r.hours) })),
  };
}

const splitPath = (opportunityId: string) => `/api/resource-splits/${encodeURIComponent(opportunityId)}`;

export async function fetchSplit(fetcher: Fetch, opportunityId: string): Promise<ResourceSplit> {
  const response = await fetcher(splitPath(opportunityId));
  if (!response.ok) throw await errorOf(response, 'Could not load the role split.');
  return parseSplit(await response.json());
}

/** Sets the deal's actual split; null resets it to the sold split. */
export async function saveSplit(fetcher: Fetch, opportunityId: string, shares: RoleShare[] | null): Promise<ResourceSplit> {
  const response = await fetcher(splitPath(opportunityId), shares === null
    ? { method: 'DELETE' }
    : { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ shares }) });
  if (!response.ok) throw await errorOf(response, 'Could not save the role split.');
  return parseSplit(await response.json());
}
